import { useEffect, useState } from 'react';
import Banner from 'components/crm/ui/Banner';
import Button from 'components/crm/ui/Button';
import CallQualification from 'components/prospection/CallQualification';
import CallStatusPill from 'components/prospection/CallStatusPill';
import ExchangeSummary from 'components/prospection/ExchangeSummary';
import useCallWindowWarning from 'components/prospection/useCallWindowWarning';
import { useDial } from 'components/record/callAction';
import useCallSheetShortcuts from 'hooks/useCallSheetShortcuts';
import useText from 'hooks/useText';
import { telHref } from 'pages/prospection/callListItems';
import { getCallHistory } from 'server/crm/prospectionApi';

const EMPTY = '-';
// The standard's number goes without a word; a member's or a contact's is said.
const PHONE_SOURCES = ['member', 'contact'];

// The call sheet: who is being called, the qualification of the call, and a
// way back to the waiting panel. Session behaviour (toast and next contact,
// pause) comes with 1397 through onConfirmed.
// history: null while loading; a failed read gives [] so that the
// qualification stays usable.
// afterConfirm: { kind: 'next' | 'summary', onClick }, the footer action that
// replaces « Fermer » once the call is confirmed; null keeps « Fermer ».
// liveCall: the Wildix call of this sheet (JBWEB-1400), or null.
// phoneCallId: the Wildix call to qualify given by the address (Home), used
// when no live call shows on the sheet.
const CallSheet = ({
  company,
  line,
  sessionId,
  onClose,
  onConfirmed,
  afterConfirm = null,
  liveCall = null,
  phoneCallId = null,
  shortcutsEnabled = true,
  callRules = null,
}) => {
  const { text } = useText('prospection');
  const href = telHref(company?.phone);
  const [history, setHistory] = useState(null);
  const [confirmedMessage, setConfirmedMessage] = useState(null);
  const [reloadCount, setReloadCount] = useState(0);
  // Kept after the confirmation, when the qualification is gone, for the pill.
  const [result, setResult] = useState(null);
  const companyId = company?.id;
  // A and the number's links dial through Wildix, with a warning outside the
  // call window; the href stays for a copy of the number.
  const dial = useDial();
  const dialWithWarning = useCallWindowWarning(callRules, dial);
  const dialLink = (phone) => (event) => {
    event.preventDefault();
    dialWithWarning(phone);
  };

  useEffect(() => {
    // An answer for a sheet closed in the meantime is ignored.
    let active = true;
    getCallHistory(companyId).then(
      (calls) => active && setHistory(calls || []),
      () => active && setHistory([]),
    );
    return () => {
      active = false;
    };
  }, [companyId, reloadCount]);

  const confirmed = ({ message, response }) => {
    setConfirmedMessage(message);
    setReloadCount((count) => count + 1);
    if (onConfirmed) onConfirmed({ message, response });
  };

  // A person called: their position (when known) and their company. The
  // company itself (its name is the title): its switchboard, no position.
  const whoParts = company?.contactName
    ? [company.contactRole, company.name].filter(Boolean)
    : company?.phone && company.phoneSource === 'company'
      ? ['Standard']
      : [];
  const footerAction = confirmedMessage ? afterConfirm : null;
  // S and Enter before the confirmation belong to the qualification.
  useCallSheetShortcuts(
    {
      a: () => dialWithWarning(company?.phone),
      enter: footerAction ? footerAction.onClick : null,
    },
    shortcutsEnabled,
  );

  return (
    <section className="flex h-full min-w-0 flex-1 flex-col rounded-2xl border border-line bg-white">
      <div className="shrink-0 p-6 pb-4">
        <div className="flex items-start justify-between gap-4">
          <h2 className="font-title text-lg font-semibold leading-6 text-ink">
            {company?.contactName || company?.name || EMPTY}
          </h2>
          <CallStatusPill
            liveCall={liveCall}
            confirmed={Boolean(confirmedMessage)}
            result={result}
          />
        </div>
        <p className="mt-1 text-sm text-muted">
          {whoParts.length > 0 && `${whoParts.join(' · ')} · `}
          {href ? (
            <a
              href={href}
              onClick={dialLink(company.phone)}
              className="text-primary underline-offset-2 hover:underline"
            >
              {company.phone}
            </a>
          ) : (
            company?.phone || EMPTY
          )}
          {company?.phone && PHONE_SOURCES.includes(company.phoneSource) ? (
            <span> ({text(`sheet.phoneSource.${company.phoneSource}`)})</span>
          ) : null}
          {/* The direct line of the person a callback names (JBWEB-1458), the standard beside it. */}
          {company?.phone && company.phoneSource === 'callbackContact' ? <span> (ligne directe)</span> : null}
          {company?.phoneSource === 'callbackContact' && telHref(line?.standardPhone) ? (
            <>
              {' · '}
              <a
                href={telHref(line.standardPhone)}
                onClick={dialLink(line.standardPhone)}
                className="text-primary underline-offset-2 hover:underline"
              >
                Appeler le standard
              </a>
            </>
          ) : null}
        </p>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 pb-4">
        {confirmedMessage ? <Banner>{confirmedMessage}</Banner> : null}
        <ExchangeSummary companyId={companyId} history={history} />
        {confirmedMessage ? null : (
          <CallQualification
            company={company}
            line={line}
            sessionId={sessionId}
            history={history}
            onConfirmed={confirmed}
            onResultChange={setResult}
            liveCall={liveCall}
            phoneCallId={liveCall?.id || phoneCallId}
            shortcutsEnabled={shortcutsEnabled}
            callRules={callRules}
          />
        )}
      </div>
      <div className="flex shrink-0 items-center justify-between gap-4 border-t border-line p-4">
        <p className="text-xs text-muted">{text('sheet.shortcuts')}</p>
        {footerAction ? (
          <Button onClick={footerAction.onClick}>
            {text(`sheet.after.${footerAction.kind}`)}
          </Button>
        ) : (
          <Button variant="secondary" onClick={onClose}>
            {text('sheet.close')}
          </Button>
        )}
      </div>
    </section>
  );
};

export default CallSheet;
